import React, { useEffect, useState } from "react";
import { FilterProps, QueryFilter } from "./Filters.types";
import { Button } from "../button";
import { Icon } from "../../modules/icon";
import { DropdownButton, ButtonGroup, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useBaseRequest from "../../api/BaseRequest";
import MetaDataGateway from "../../api/gateways/MetaDataGateway";
import { IMetaDataItem, MetaDataType } from "../../entities/IMetaData";
import { Language } from "../translations/language";

interface DocumentDataField {
  item: IMetaDataItem;
  isChecked: boolean;
  inputValue: string;
}

export function DocumentDataFilter({ filter, onRemove, onFilterSet }: FilterProps) {
  const { t, i18n } = useTranslation();
  const [fields, setFields] = useState<DocumentDataField[]>([]);
  const { execute: fetchItems, data: metaDataItems } = useBaseRequest(MetaDataGateway.getAllMetaDataItems);

  useEffect(() => {
    fetchItems(undefined);
  }, []);

  useEffect(() => {
    if (!metaDataItems) return;
    const sorted = [...metaDataItems]
      .filter((item) => item.enableForFiltering !== false)
      .sort((a, b) => new Date((b as any).createdAt).getTime() - new Date((a as any).createdAt).getTime());
    setFields((prev) =>
      sorted.map((item) => {
        const existing = prev.find((f) => f.item.key === item.key);
        return existing ? { ...existing, item } : { item, isChecked: false, inputValue: "" };
      })
    );
  }, [metaDataItems]);

  useEffect(() => {
    if (!filter.defaultValues || !Array.isArray(filter.defaultValues) || fields.length === 0) return;
    const defaultData = filter.defaultValues as any[];
    setFields((prev) =>
      prev.map((f) => {
        const defaultValue = defaultData.find((d) => d.field === f.item.key);
        if (!defaultValue) return f;
        return { ...f, isChecked: true, inputValue: defaultValue.value || "" };
      })
    );
  }, [filter.defaultValues, fields.length]);

  const getLabel = (item: IMetaDataItem): string =>
    item.translations?.[i18n.language as Language] ||
    item.translations?.["en" as Language] ||
    item.name;

  const handleCheckboxChange = (key: string) => {
    setFields((prev) => prev.map((f) => (f.item.key === key ? { ...f, isChecked: !f.isChecked } : f)));
  };

  const handleInputChange = (key: string, value: string) => {
    setFields((prev) => prev.map((f) => (f.item.key === key ? { ...f, inputValue: value } : f)));
  };

  const applyFilter = () => {
    const activeFields = fields.filter((f) => f.isChecked && f.inputValue.trim());
    if (activeFields.length === 0) {
      onFilterSet([], filter);
      return;
    }
    const queryFilters: QueryFilter[] = activeFields.map(
      (f) =>
        ({
          name: f.item.key,
          type: "documentMetaData",
          field: f.item.key,
          value: JSON.stringify(f.inputValue),
        } as any)
    );
    onFilterSet(queryFilters, filter);
  };

  const renderAppliedNames = (): string => {
    const activeFields = fields.filter((f) => f.isChecked && f.inputValue.trim());
    if (activeFields.length === 0) return "";
    return activeFields.map((f) => `${getLabel(f.item)}: ${f.inputValue}`).join(", ");
  };

  const isDateType = (item: IMetaDataItem) =>
    (item.dataType as string) === MetaDataType.DATE || (item.dataType as string) === "datetime";

  return (
    <div className="mr-3 my-1">
      <div className="btn-group">
        <DropdownButton
          as={ButtonGroup}
          variant="outline-dark"
          onSelect={() => true}
          title={
            <>
              {t(filter.name)}: {!!fields.some((f) => f.isChecked) && renderAppliedNames()}
              &nbsp;
            </>
          }
        >
          <div>
            <div className="px-2 pt-2" style={{ maxHeight: "320px", overflowY: "auto" }}>
              {fields.length === 0 && (
                <div className="px-2 py-2 text-muted" style={{ fontSize: 13 }}>
                  {t("LOADING")}...
                </div>
              )}
              {fields.map((field, key) => (
                <div key={key} className="mb-3">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`doc-data-${field.item.key}`}
                      checked={field.isChecked}
                      onChange={() => handleCheckboxChange(field.item.key)}
                    />
                    <label htmlFor={`doc-data-${field.item.key}`} className="form-check-label">
                      {getLabel(field.item)}
                    </label>
                  </div>
                  {field.isChecked && (
                    <input
                      type={isDateType(field.item) ? "date" : "text"}
                      className="form-control form-control-sm"
                      value={field.inputValue}
                      onChange={(e) => handleInputChange(field.item.key, e.target.value)}
                      placeholder={getLabel(field.item)}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="apply" style={{ position: "sticky", bottom: 0 }}>
              <Dropdown.Item className="btn btn-primary mt-3" onClick={applyFilter}>
                {t("APPLY")}
              </Dropdown.Item>
            </div>
          </div>
        </DropdownButton>
        <Button className="btn btn-outline-dark" onClick={() => onRemove(filter)}>
          <Icon title="Remove" type="fa-solid fa-xmark" size={16} />
        </Button>
      </div>
    </div>
  );
}
